<template>
  <div class="addressForm">
    <div class="address-row mb-2 flex-col md:flex-row md:items-start">
      <div class="address-searcher flex-1 mb-2 mr-3 gap-3">
        <div class="address-search-header flex-2">
          <span
            class="d-flex flex-1 align-items-center font-bold form-control-label"
          >
            <i class="fa fa-search mr-1" />
            Search Address
          </span>
          <AddressManualEntryLink
            v-if="!hideManualEntryLink && selectedAddress.primary"
            class="d-flex align-items-right font-bold form-control-label"
            :is-manual-entry="showManualInput"
            @toggle-manual-entry="toggleManualEntry"
          />
        </div>
        <form autocomplete="off" @submit.prevent>
          <vue-google-autocomplete
            :id="'address-search-' + addressType"
            classname="form-control"
            placeholder="Start typing address.."
            country="us"
            data-cy="address-autocomplete"
            @inputChange="updateTypedAddress"
            @placechanged="getAddressData"
          />
        </form>
        <span v-if="!!notCoveredText" class="error-text text-danger">{{
          notCoveredText
        }}</span>
      </div>
      <div class="flex-1 d-flex flex-row">
        <TextField
          v-model="selectedAddress.unit"
          label="Unit Number (Optional)"
          name="unit"
          placeholder="Apt, suite, unit, etc."
          class="flex-1"
        />
        <ToggleSwitch
          v-if="!hideBillingAddressToggle"
          v-model="selectedAddress.primary"
          label-on="Billing Address"
          class="check-box-primary flex-1 ml-3 mt-3 whitespace-nowrap"
          :disabled="primaryDisabled"
        />
      </div>
    </div>
    <AddressManualInput
      ref="addressManualInput"
      :class="{ hidden: !showManualInput }"
      :selected-address="manualInputAddress"
      :chose-to-enter-manually="choseToEnterManually"
      :typed-address="typedAddress"
    />
    <div class="d-flex flex-row">
      <div class="name-container flex-1 mb-2 mr-3">
        <TextField
          v-model="selectedAddress.name"
          name="Location Name"
          :errorMessage="
            v$.selectedAddress.name
              ? firstErrorMessage(v$.selectedAddress.name)
              : ''
          "
          label-class="flex-1 mb-2"
        ></TextField>
      </div>

      <label class="flex-1 block form-group mb-2">
        <Select
          v-model="selectedLocationType"
          name="Location Type"
          placeholder="Select Location Type"
          :options="locationTypes"
          :field-error="
            v$.selectedLocationType
              ? firstErrorMessage(v$.selectedLocationType)
              : ''
          "
          full-width
          data-cy="location-type"
        />
      </label>
    </div>
    <slot name="tax-fields"></slot>
    <AdditionalContacts ref="additionalContacts" />
  </div>
</template>

<script>
import { nextTick } from "vue";
import VueGoogleAutocomplete from "vue-google-autocomplete";
import { mapActions, mapGetters, mapState } from "vuex";
import { getProperty } from "@/lib/helpers.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import TextField from "@/components/TextField.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import { locationTypeLabel } from "@/mixins";
import { Address } from "@/typings/address";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import AdditionalContacts from "./AdditionalContacts.vue";
import AddressManualEntryLink from "./AddressManualEntryLink.vue";
import AddressManualInput from "./AddressManualInput.vue";
import addressValidator from "./addressValidator.js";
import { useSelectedAddress } from "./useSelectedAddress";

export default {
  components: {
    VueGoogleAutocomplete,
    TextField,
    ToggleSwitch,
    AddressManualInput,
    AdditionalContacts,
    AddressManualEntryLink
  },
  props: {
    address: {
      type: Object,
      default: null
    },
    addressType: {
      type: String,
      default: "service"
    },
    hideBillingAddressToggle: {
      type: Boolean,
      default: false
    },
    hideManualEntryLink: {
      type: Boolean,
      default: false
    },
    isPrimary: {
      type: Boolean,
      default: false
    },
    getCustomerData: {
      type: Function,
      required: false,
      default: null
    }
  },
  validations() {
    return {
      selectedAddress: {
        name: {
          required: helpers.withMessage("Location name is required", required),
          $autoDirty: true
        }
      },
      selectedLocationType: {
        required: helpers.withMessage("Location type is required", required),
        $autoDirty: true
      }
    };
  },
  setup() {
    const { selectedAddress, toJSON: selectedAddressToJSON } =
      useSelectedAddress(true);
    return {
      selectedAddress,
      selectedAddressToJSON,
      v$: useVuelidate()
    };
  },
  data() {
    return {
      selectedLocationType: "",
      typedAddress: "",
      searchAddress: null,
      isAreaCovered: true,
      showManualInput: false,
      choseToEnterManually: false,
      isEditing: false,
      // Necessary because inputChange event is fired off after placechanged
      lastGoogleEvent: ""
    };
  },
  computed: {
    ...mapState({
      currentUser: "currentUser",
      primaryDisabled() {
        return this.isEditing && this.address.primary;
      }
    }),
    ...mapGetters(["featureEnabledForTenant"]),
    notCoveredText() {
      return !this.isAreaCovered
        ? `Zip code ${this.selectedAddress.zip_code} is not in our coverage area!`
        : "";
    },
    manualInputAddress() {
      return this.searchAddress || this.address || {};
    },
    locationTypes() {
      const options = [
        {
          value: "Residential",
          label: "Residential"
        },
        {
          value: "Commercial",
          label: "Commercial"
        },
        {
          value: "Other",
          label: "Other"
        }
      ];
      return options;
    }
  },
  watch: {
    "selectedAddress.receive_communications": function (val) {
      if (!val) return;
      if (!this.selectedAddress.additional_contacts.length) {
        this.selectedAddress.additional_contacts.push({
          email: "",
          phone: ""
        });
      }
    },
    isPrimary(newValue) {
      this.selectedAddress.primary = newValue;
    }
  },
  mounted() {
    if (this.address && this.address.id) {
      this.showManualInput = true;
      this.choseToEnterManually = this.address.manual_entry;
      this.isEditing = true;
      this.selectedAddress = new Address(this.address);
      this.selectedLocationType = locationTypeLabel(this.address) || "";
    } else if (!this.currentUser) {
      // set primary to true if creating first address
      this.selectedAddress = new Address({
        ...this.selectedAddress,
        primary: this.isPrimary
      });
    }
  },
  methods: {
    firstErrorMessage,
    ...mapActions(["validateCoverageArea"]),
    setAddressNotFoundError(hasError) {
      if (this.$refs.addressManualInput) {
        this.$refs.addressManualInput.setAddressNotFoundError(hasError);
      }
    },
    async validate() {
      const validChecks = [await this.v$.$validate()];
      if (this.showManualInput) {
        validChecks.push(await this.$refs.addressManualInput.validate());
      }
      return validChecks.every((v) => v === true);
    },
    updateTypedAddress(input) {
      this.typedAddress = input.newVal;
      // If the input manually changes, reset the searchAddress so that the validation fails
      // and the user has to select an address
      if (this.lastGoogleEvent === "inputchanged") this.searchAddress = null;
      this.lastGoogleEvent = "inputchanged";
    },
    getAddressData(addressData) {
      this.lastGoogleEvent = "placechanged";
      const formattedAddressData = addressValidator.formatAddress(addressData);
      this.selectedAddress = new Address({
        ...this.selectedAddress,
        ...formattedAddressData,

        // If we used the location search, it's no longer pinned or manually entered
        pinned: false,
        manual_entry: false
      });
      this.searchAddress = formattedAddressData;
      this.performZipCodeValidation();
      this.validateAddressIsComplete();
      this.choseToEnterManually = false;
      this.showManualInput = false;
    },
    async performZipCodeValidation() {
      const validationData = {
        email: getProperty(this.currentUser, "email") || "no-reply@sera.tech",
        zip_code: this.selectedAddress.zip_code
      };

      if (this.getCustomerData) {
        try {
          const customerData = await this.getCustomerData();
          if (
            customerData &&
            customerData.emails &&
            customerData.emails.length > 0
          ) {
            validationData.email = customerData.emails[0]?.email_address || "";

            if (customerData.phones && customerData.phones.length > 0) {
              validationData.phone = customerData.phones[0]?.phone_number || "";
            }
          }
        } catch (error) {
          // Just catch the error, we don't need to do anything with it
        }
      }

      try {
        await this.validateCoverageArea(validationData);
        this.isAreaCovered = true;
      } catch {
        this.isAreaCovered = false;
      }
    },
    toggleManualEntry() {
      this.showManualInput = !this.showManualInput;
      this.choseToEnterManually = this.showManualInput;
    },
    validateAddressIsComplete() {
      const missingProps = addressValidator.validateAddress(
        this.selectedAddress
      );
      this.showManualInput = missingProps.length > 0;

      nextTick().then(() => {
        setTimeout(() => {
          if (this.showManualInput) {
            this.v$.validate();
          }
        }, 100);
      });
    },
    async getFormData() {
      const isValid = await this.validate();
      // Apply the same client-side gate to both new and existing addresses.
      // When editing a non-geocodable address the autocomplete can leave
      // `selectedAddress.street` blank; without this gate the form posts
      // an empty street and the server rejects the save with
      // "Street can't be blank". Surfacing the manual panel up-front lets
      // the user fix the missing field inline instead.
      if (!isValid) {
        this.showManualInput = addressValidator.checkForAddressInputErrors(
          this.v$.$errors
        );
        return;
      }
      let formData = {
        ...this.selectedAddressToJSON(),
        ...this.locationTypeFlags(this.selectedLocationType)
      };
      if (this.showManualInput && this.$refs.addressManualInput) {
        formData = {
          ...formData,
          ...(await this.$refs.addressManualInput.getFormData())
        };
      }
      return formData;
    }
  }
};
</script>

<style lang="scss">
.addressForm {
  .ss-input-field {
    box-shadow: none;
    min-height: 36px !important;
  }
}
.address-search-header {
  display: flex;
}
.address-row {
  display: flex;
  flex-direction: row;
}

.pac-container {
  z-index: 1100;
}

.name-container {
  display: flex;
}

.location-name-input {
  width: 75%;
}

.input-error-background {
  background-color: rgb(247, 229, 229) !important;
  border: 1px solid #dc3545;
}

.error-text {
  color: red;
  display: block;
}
</style>
